/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import './BookingList.css';
import { Booking } from '../../types/booking';
import { Employee } from '../../types/employee';
import { Service } from '../../types/service';
import { CalendarClockLargeIcon } from '../Navigation/NavIcons';

import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

function BookingList() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const canEdit = user?.role !== 'staff';

  const [displayBookings, setDisplayBookings] = useState<Booking[]>([]);
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [staffs, setStaffs] = useState<Employee[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    today: 0, week: 0, month: 0, total: 0,
    online: 0, manual: 0,
    pending: 0, confirmed: 0, completed: 0, cancelled: 0
  });
  const [filters, setFilters] = useState({
    timeRange: 'all',
    source: 'all',
    status: 'all',
    search: ''
  });

  // Detail modal
  const [viewDetail, setViewDetail] = useState<Booking | null>(null);

  // Assign modal
  const [assignBooking, setAssignBooking] = useState<Booking | null>(null);
  const [assignEmpId, setAssignEmpId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Image lightbox (preview full-size customer photo)
  const [previewImg, setPreviewImg] = useState<string | null>(null);

  // Checkout / Complete modal (ghi nhận doanh thu + TIP)
  const [checkoutBooking, setCheckoutBooking] = useState<Booking | null>(null);
  const [checkoutPrice, setCheckoutPrice] = useState(0);
  const [checkoutTip, setCheckoutTip] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // State cho service forms
  const [showAddServiceForm, setShowAddServiceForm] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServicePrice, setNewServicePrice] = useState(0);
  const [newServiceColor, setNewServiceColor] = useState('#667eea');
  const [newServiceDuration, setNewServiceDuration] = useState(30);
  const [newServiceCategory, setNewServiceCategory] = useState('Other');
  const [newServiceIsBestSeller, setNewServiceIsBestSeller] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);

  // States and actions for service grouping
  const [emptyGroups, setEmptyGroups] = useState<string[]>([]);
  const [activeGroupAction, setActiveGroupAction] = useState<string | null>(null);

  const handleCreateNewGroup = () => {
    const groupName = prompt(language === 'vi' ? 'Nhập tên nhóm mới:' : 'Enter new group name:');
    if (groupName && groupName.trim()) {
      const trimmed = groupName.trim();
      if (!emptyGroups.includes(trimmed)) {
        setEmptyGroups([...emptyGroups, trimmed]);
      }
      setNewServiceCategory(trimmed);
      setShowAddServiceForm(true);
      // Wait for DOM to update then scroll to form
      setTimeout(() => {
        const formEl = document.querySelector('.add-service-form');
        if (formEl) {
          formEl.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleAddServiceToCategory = (categoryName: string) => {
    setNewServiceCategory(categoryName);
    setShowAddServiceForm(true);
    // Clear other fields to start fresh
    setNewServiceName('');
    setNewServicePrice(0);
    setNewServiceColor('#667eea');
    setNewServiceDuration(30);
    setNewServiceIsBestSeller(false);
    
    setTimeout(() => {
      const formEl = document.querySelector('.add-service-form');
      if (formEl) {
        formEl.scrollIntoView({ behavior: 'smooth' });
        // Focus the name input
        const nameInput = formEl.querySelector('input[type="text"]') as HTMLInputElement;
        if (nameInput) nameInput.focus();
      }
    }, 100);
  };

  const handleMoveServiceToCategory = async (serviceId: string | number, targetCategory: string) => {
    const service = services.find(s => s.id?.toString() === serviceId?.toString());
    if (!service) return;
    
    const updatedService = { ...service, category: targetCategory };
    try {
      await apiService.updateService(serviceId, updatedService);
      setServices(services.map(s => s.id?.toString() === serviceId?.toString() ? updatedService : s));
      // Remove from emptyGroups list if it was there
      setEmptyGroups(emptyGroups.filter(g => g !== targetCategory));
      alert(language === 'vi' 
        ? `✅ Đã gộp dịch vụ "${service.name}" vào nhóm "${targetCategory}"`
        : `✅ Successfully grouped "${service.name}" into "${targetCategory}"`
      );
    } catch (error) {
      console.error('Error moving service:', error);
      // Fallback local storage
      const updatedServices = services.map(s => s.id?.toString() === serviceId?.toString() ? updatedService : s);
      setServices(updatedServices);
      localStorage.setItem('salonServices', JSON.stringify(updatedServices));
      setEmptyGroups(emptyGroups.filter(g => g !== targetCategory));
      alert(language === 'vi'
        ? `⚠️ Đã lưu thay đổi vào Local Storage (Server offline)`
        : `⚠️ Saved to Local Storage (Server offline)`
      );
    }
  };

  const handleDeleteGroup = async (categoryName: string) => {
    if (categoryName === 'Other') {
      alert(language === 'vi' ? 'Không thể xóa nhóm mặc định Other' : 'Cannot delete default group Other');
      return;
    }

    const confirmMsg = language === 'vi'
      ? `Bạn có chắc chắn muốn xóa nhóm "${categoryName}"? Tất cả dịch vụ trong nhóm này sẽ được chuyển sang nhóm "Other".`
      : `Are you sure you want to delete the group "${categoryName}"? All services in this group will be moved to the "Other" group.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      // Find all services in this category
      const servicesToMove = services.filter(s => ((s as any).category || 'Other') === categoryName);
      
      // Update each service's category to 'Other'
      const updatePromises = servicesToMove.map(s => 
        apiService.updateService(s.id, { ...s, category: 'Other' })
      );
      
      await Promise.all(updatePromises);
      
      // Update local services state
      const updatedServices = services.map(s => 
        ((s as any).category || 'Other') === categoryName 
          ? { ...s, category: 'Other' } 
          : s
      );
      setServices(updatedServices);
      
      // Remove from emptyGroups list if it was there
      setEmptyGroups(emptyGroups.filter(g => g !== categoryName));
      
      alert(language === 'vi' 
        ? `✅ Đã xóa nhóm "${categoryName}" thành công!`
        : `✅ Deleted group "${categoryName}" successfully!`
      );
    } catch (error) {
      console.error('Error deleting group:', error);
      // Fallback local storage
      const updatedServices = services.map(s => 
        ((s as any).category || 'Other') === categoryName 
          ? { ...s, category: 'Other' } 
          : s
      );
      setServices(updatedServices);
      localStorage.setItem('salonServices', JSON.stringify(updatedServices));
      setEmptyGroups(emptyGroups.filter(g => g !== categoryName));
      
      alert(language === 'vi'
        ? `⚠️ Đã cập nhật vào Local Storage (Server offline)`
        : `⚠️ Updated in Local Storage (Server offline)`
      );
    }
  };

  const navigate = useNavigate();

  // Màu sắc mặc định cho dịch vụ
  const serviceColors = [
    '#3B82F6', '#60A5FA', '#93C5FD', '#1D4ED8', '#8B5CF6', '#A78BFA', '#4C1D95',
    '#10B981', '#34D399', '#6EE7B7', '#064E3B', '#059669', '#14B8A6', '#5EEAD4',
    '#EF4444', '#F87171', '#FCA5A5', '#B91C1C', '#EC4899', '#F472B6', '#831843',
    '#F59E0B', '#FBBF24', '#FDE68A', '#D97706', '#F97316', '#FB923C', '#7C2D12',
    '#6366F1', '#818CF8', '#C7D2FE', '#4338CA', '#9F1239', '#E11D48', '#FDA4AF',
    '#06B6D4', '#22D3EE', '#A5F3FC', '#0369A1', '#0EA5E9', '#7DD3FC', '#0C4A6E'
  ];

  // Tải dữ liệu từ API
  useEffect(() => {
    const fetchData = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        // Fetch tất cả dữ liệu đồng thời
        const [bookingsResponse, staffsResponse, servicesResponse] = await Promise.all([
          apiService.getBookings(),
          apiService.getEmployees(),
          apiService.getServices()
        ]);

        // apiService already returns parsed JSON, no need to check .ok or call .json()
        const bookings = bookingsResponse.data || bookingsResponse || [];
        const staffsList = staffsResponse.data || staffsResponse || [];
        let servicesList = servicesResponse.data || servicesResponse || [];

        // MERGE: Combine API services with LocalStorage services (Client-side extensions)
        try {
          const localServicesStr = localStorage.getItem('salonServices');
          if (localServicesStr) {
            const localServices = JSON.parse(localServicesStr);
            // Merge, avoiding duplicates (prefer local or API? Usually distinct IDs. API has numbers, Local has Date.now())
            // We'll just append local ones that aren't in API list by ID
            const apiIds = new Set(servicesList.map((s: Service) => s.id));
            const uniqueLocal = localServices.filter((s: Service) => !apiIds.has(s.id));
            servicesList = [...servicesList, ...uniqueLocal];
          }
        } catch (e) {
          console.warn('Failed to merge local services', e);
        }

        setAllBookings(bookings);
        setStaffs(staffsList);
        setServices(servicesList);
        calculateStats(bookings);
      } catch (error) {
        console.error('Error loading data:', error);
        // Fallback: thử lấy từ localStorage nếu API fail
        if (!silent) {
          try {
            const savedBookings = localStorage.getItem('salonBookings');
            const bookingsData = savedBookings ? JSON.parse(savedBookings) : [];
            setAllBookings(bookingsData);

            const savedStaffs = localStorage.getItem('salonStaffs');
            setStaffs(savedStaffs ? JSON.parse(savedStaffs) : []);

            const savedServices = localStorage.getItem('salonServices');
            setServices(savedServices ? JSON.parse(savedServices) : []);

            calculateStats(bookingsData);
          } catch (localStorageError) {
            console.error('LocalStorage fallback failed:', localStorageError);
          }
        }
      } finally {
        if (!silent) setLoading(false);
      }
    };

    fetchData();

    // Refresh ngầm mỗi 60 giây, không trigger loading state
    const interval = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(interval);
  }, []);

  // Helper: lay dung ten truong tu backend
  const getBookingDate = (b: Booking) => b.bookingDate || b.date || '';
  const getPhone = (b: Booking) => b.customerPhone || b.phone || '';
  const getService = (b: Booking) => b.serviceType || b.service || '';
  const getNotes = (b: Booking) => b.specialRequests || b.notes || '';
  // Parse YYYY-MM-DD as local date (not UTC) to avoid timezone shift
  const parseLocalDate = (dateStr: string) => {
    if (!dateStr) return new Date();
    const match = String(dateStr).match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    return new Date(dateStr);
  };

  const getEmployeeName = (b: Booking) => {
    if (b.staffName) return b.staffName;
    if (b.employeeId && b.employeeId !== 'unassigned') {
      const found = staffs.find(s => (s.id || (s as any)._id)?.toString() === b.employeeId?.toString());
      if (found) return found.name;
      // If not found and the ID is a MongoDB ObjectId (24 hex chars), it means the employee was deleted
      if (/^[0-9a-fA-F]{24}$/.test(b.employeeId.toString())) {
        return language === 'vi' ? '(Nhân viên đã xóa)' : '(Deleted Staff)';
      }
      return b.employeeId;
    }
    return null;
  };

  // Tinh thong ke
  const calculateStats = (bookingsData: Booking[]) => {
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-CA'); // YYYY-MM-DD local

    const todayB = bookingsData.filter(b => getBookingDate(b) === todayStr);
    const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(now); monthAgo.setMonth(monthAgo.getMonth() - 1);
    const weekB = bookingsData.filter(b => parseLocalDate(getBookingDate(b)) >= weekAgo);
    const monthB = bookingsData.filter(b => parseLocalDate(getBookingDate(b)) >= monthAgo);

    setStats({
      today: todayB.length,
      week: weekB.length,
      month: monthB.length,
      total: bookingsData.length,
      online: bookingsData.filter(b => b.source === 'online').length,
      manual: bookingsData.filter(b => b.source !== 'online').length,
      pending: bookingsData.filter(b => b.status === 'pending').length,
      confirmed: bookingsData.filter(b => b.status === 'confirmed').length,
      completed: bookingsData.filter(b => b.status === 'completed').length,
      cancelled: bookingsData.filter(b => b.status === 'cancelled').length,
    });
  };

  // Apply filters using correct backend field names
  useEffect(() => {
    let filtered = [...allBookings];

    if (filters.timeRange !== 'all') {
      const now = new Date();
      const todayStr = now.toLocaleDateString('en-CA');
      switch (filters.timeRange) {
        case 'today':
          filtered = filtered.filter(b => getBookingDate(b) === todayStr);
          break;
        case 'week': {
          const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
          filtered = filtered.filter(b => parseLocalDate(getBookingDate(b)) >= weekAgo);
          break;
        }
        case 'month': {
          const monthAgo = new Date(); monthAgo.setMonth(monthAgo.getMonth() - 1);
          filtered = filtered.filter(b => parseLocalDate(getBookingDate(b)) >= monthAgo);
          break;
        }
      }
    }

    if (filters.source !== 'all') {
      filtered = filtered.filter(b => b.source === filters.source);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(b => b.status === filters.status);
    }

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      const isNumeric = /^\d+$/.test(q); // Check if only numbers

      filtered = filtered.filter(b => {
        const phone = getPhone(b).trim();
        const phoneClean = phone.replace(/\D/g, '');
        const name = (b.customerName || '').toLowerCase().trim();

        // Ưu tiên cực cao: Trùng khớp chính xác tuyến đối 100% tên hoặc SĐT
        if (phone === q || name === q || phoneClean === q) {
            return true;
        }

        // Nếu là tìm kiếm SĐT (chứa toàn số), yêu cầu phải khớp chính xác
        if (isNumeric && q.length > 3) {
             return phoneClean === q || phoneClean.includes(q);
        }

        return name.includes(q) ||
          phone.includes(q) ||
          getService(b).toLowerCase().includes(q) ||
          (getEmployeeName(b) || '').toString().toLowerCase().includes(q);
      });
    }

    // Sort: Newest created or updated first
    filtered.sort((a, b) => {
      // Ưu tiên ngày tạo/cập nhật, mới nhất lên trên
      const timeA = new Date(a.createdAt || a.date).getTime();
      const timeB = new Date(b.createdAt || b.date).getTime();
      return timeB - timeA;
    });

    setDisplayBookings(filtered);
  }, [allBookings, filters, staffs]);

  // Format ngày — parse YYYY-MM-DD as local time to avoid timezone shift
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = parseLocalDate(dateStr);
    const locale = language === 'vi' ? 'vi-VN' : 'en-US';
    return date.toLocaleDateString(locale, {
      weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  // Cap nhat status (with checkout interception)
  const handleStatusChange = (id: string, newStatus: string, booking: Booking) => {
    if (newStatus === 'completed') {
      // Show price/tip modal before marking complete
      setCheckoutBooking(booking);
      // Pre-fill with existing price if already set
      setCheckoutPrice((booking as any).price || 0);
      setCheckoutTip((booking as any).tip || 0);
    } else {
      updateBookingStatus(id, newStatus);
    }
  };

  const updateBookingStatus = async (id: string, newStatus: string, extraData?: { price?: number; tip?: number; totalAmount?: number }) => {
    try {
      await apiService.updateBooking(id as any, { status: newStatus, ...extraData });
      const updated = allBookings.map(b =>
        (b.id || (b as any)._id)?.toString() === id.toString() ? { ...b, status: newStatus as any, ...extraData } : b
      );
      setAllBookings(updated);
      calculateStats(updated);
      // Update detail modal if open
      if (viewDetail && (viewDetail.id || (viewDetail as any)._id)?.toString() === id.toString()) {
        setViewDetail({ ...viewDetail, status: newStatus as any });
      }
    } catch (err: any) {
      alert(language === 'vi'
        ? `⚠️ Không thể cập nhật: ${err.message}`
        : `⚠️ Cannot update: ${err.message}`
      );
    }
  };

  const handleCheckoutConfirm = async () => {
    if (!checkoutBooking) return;
    const id = ((checkoutBooking.id || (checkoutBooking as any)._id) as any).toString();
    const total = checkoutPrice + checkoutTip;
    setCheckoutLoading(true);
    try {
      await updateBookingStatus(id, 'completed', {
        price: checkoutPrice,
        tip: checkoutTip,
        totalAmount: total
      });
      setCheckoutBooking(null);
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Phan cong nhan vien
  const handleAssignEmployee = async () => {
    if (!assignBooking) return;
    const bookingId = (assignBooking.id || (assignBooking as any)._id)?.toString();
    const emp = staffs.find(s => (s.id || (s as any)._id)?.toString() === assignEmpId);
    try {
      setAssigning(true);
      await apiService.updateBooking(bookingId as any, {
        employeeId: assignEmpId,
        staffName: emp?.name || ''
      });
      const updated = allBookings.map(b =>
        (b.id || (b as any)._id)?.toString() === bookingId
          ? { ...b, employeeId: assignEmpId, staffName: emp?.name || '' }
          : b
      );
      setAllBookings(updated);
      calculateStats(updated);
      setAssignBooking(null);
      setAssignEmpId('');
    } catch (err: any) {
      alert(language === 'vi'
        ? `⚠️ Không thể phân công: ${err.message}`
        : `⚠️ Cannot assign: ${err.message}`
      );
    } finally {
      setAssigning(false);
    }
  };


  // Xóa booking - SỬA LẠI ĐỂ GỌI API
  const deleteBooking = async (id: number) => {
    if (!window.confirm(t('bookings.messages.confirmDelete'))) return;

    try {
      // Gọi API để xóa
      await apiService.deleteBooking(id);



      // Cập nhật state
      const updatedBookings = allBookings.filter(booking => (booking.id || (booking as any)._id)?.toString() !== id?.toString());
      setAllBookings(updatedBookings);
      calculateStats(updatedBookings);

    } catch (error) {
      console.error('Error deleting booking:', error);
      // Fallback
      const updatedBookings = allBookings.filter(booking => (booking.id || (booking as any)._id)?.toString() !== id?.toString());

      setAllBookings(updatedBookings);
      localStorage.setItem('salonBookings', JSON.stringify(updatedBookings));
      calculateStats(updatedBookings);

      alert(language === 'vi'
        ? '⚠️ Đã xóa cục bộ (Server offline).'
        : '⚠️ Deleted locally (Server offline).'
      );
    }
  };

  const handleAddService = async () => {
    if (newServiceName.trim() === '') {
      alert(t('services.messages.enterName'));
      return;
    }

    // Check if service with the same name already exists in another category
    const normalizedName = newServiceName.trim().toLowerCase();
    const targetCategory = newServiceCategory.trim() || 'Other';
    const existingService = services.find(s => s.name.trim().toLowerCase() === normalizedName);

    if (existingService) {
      const currentCategory = (existingService as any).category || 'Other';
      if (currentCategory.toLowerCase() !== targetCategory.toLowerCase()) {
        const confirmTransfer = window.confirm(language === 'vi'
          ? `Dịch vụ "${existingService.name}" đã tồn tại trong nhóm "${currentCategory}". Bạn có muốn chuyển nó sang nhóm mới "${targetCategory}" không?`
          : `Service "${existingService.name}" already exists in group "${currentCategory}". Do you want to move it to the new group "${targetCategory}"?`
        );
        if (confirmTransfer) {
          // Transfer existing service to new category
          await handleMoveServiceToCategory(existingService.id, targetCategory);
          
          // Clear inputs and close form
          setNewServiceName('');
          setNewServicePrice(0);
          setNewServiceColor('#667eea');
          setNewServiceCategory('Other');
          setNewServiceDuration(30);
          setNewServiceIsBestSeller(false);
          setShowAddServiceForm(false);
          return;
        } else {
          // If they don't want to transfer, stop here so we don't create a duplicate
          return;
        }
      } else {
        alert(language === 'vi'
          ? `Dịch vụ "${existingService.name}" đã có sẵn trong nhóm này!`
          : `Service "${existingService.name}" already exists in this group!`
        );
        return;
      }
    }

    try {
      const newServiceData = {
        name: newServiceName.trim(),
        price: Number(newServicePrice),
        color: newServiceColor,
        category: newServiceCategory.trim() || 'Other',
        duration: Number(newServiceDuration) || 30,
        isBestSeller: newServiceIsBestSeller,
      };

      const response = await apiService.createService(newServiceData);

      // Handle response properly
      const savedService = response.data || response;

      if (!savedService || !savedService.id) {
        throw new Error('Invalid response from server');
      }

      setServices([...services, savedService]);
      setNewServiceName('');
      setNewServicePrice(0);
      setNewServiceColor('#667eea');
      setNewServiceCategory('Other');
      setNewServiceDuration(30);
      setNewServiceIsBestSeller(false);
      setShowAddServiceForm(false);
      alert(t('services.messages.addSuccess'));
    } catch (error: any) {
      console.error('Error adding service:', error);

      // Show detailed error instead of silent fallback
      const errorMessage = error.message || 'Unknown error';

      if (window.confirm(
        `❌ Cannot connect to server: ${errorMessage}\n\n` +
        'Would you like to save this service offline? (It will sync when server is available)'
      )) {
        // Only save offline if user explicitly allows it
        const fallbackService = {
          id: Date.now(),
          name: newServiceName.trim(),
          price: Number(newServicePrice),
          color: newServiceColor,
          category: newServiceCategory.trim() || 'Other',
          duration: Number(newServiceDuration) || 30,
        } as Service;

        const updatedServices = [...services, fallbackService];
        setServices(updatedServices);
        localStorage.setItem('salonServices', JSON.stringify(updatedServices));
        setNewServiceName('');
        setNewServicePrice(0);
        setNewServiceColor('#667eea');
        setNewServiceCategory('Other');
        setNewServiceDuration(30);
        setNewServiceIsBestSeller(false);
        setShowAddServiceForm(false);
        alert(language === 'vi'
          ? '⚠️ Đã lưu ngoại tuyến. Sẽ đồng bộ khi server hoạt động trở lại.'
          : '⚠️ Saved offline. Will sync when server is back online.'
        );
      }
    }
  };

  // Cập nhật dịch vụ
  const handleUpdateService = async () => {
    if (!editingService || editingService.name.trim() === '') {
      alert(t('services.messages.enterName'));
      return;
    }

    try {
      const response = await apiService.updateService(editingService.id, editingService);
      const updated = response.data || response;
      setServices(services.map(s => s.id === updated.id ? updated : s));
      setEditingService(null);
      alert(t('services.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating service:', error);
      const updatedServices = services.map(s => s.id === editingService.id ? editingService : s);
      setServices(updatedServices);
      localStorage.setItem('salonServices', JSON.stringify(updatedServices));
      setEditingService(null);
      alert(t('services.messages.updateOffline'));
    }
  };

  // Xóa dịch vụ
  const handleDeleteService = async (serviceId: string | number) => {
    if (!window.confirm(t('services.messages.confirmDelete'))) {
      return;
    }

    try {
      await apiService.deleteService(serviceId);
      setServices(services.filter(s => s.id?.toString() !== serviceId?.toString()));
    } catch (error) {
      console.error('Error deleting service:', error);
      const updatedServices = services.filter(service => service.id?.toString() !== serviceId?.toString());
      setServices(updatedServices);
      localStorage.setItem('salonServices', JSON.stringify(updatedServices));

      const updatedBookings = allBookings.map(booking => {
        if (booking.serviceIds && booking.serviceIds.includes(serviceId.toString())) {

          return {
            ...booking,
            service: t('services.deletedService')
          };
        }
        return booking;
      });

      setAllBookings(updatedBookings);
      localStorage.setItem('salonBookings', JSON.stringify(updatedBookings));

      alert(language === 'vi'
        ? '⚠️ Đã xóa cục bộ (Server offline).'
        : '⚠️ Deleted locally (Server offline).'
      );
    }
  };


  // Xuất dữ liệu booking sang Excel (CSV format)
  const exportBookings = () => {
    // 1. Dựng tiêu đề cột bằng tiếng Việt (hoặc theo language context nếu cần, nhưng fixed tiếng Việt cho dễ hiểu)
    const headers = ['Mã Đặt Lịch', 'Ngày', 'Giờ', 'Khách Hàng', 'SĐT', 'Dịch Vụ', 'Nhân Viên', 'Trạng Thái', 'Nguồn Booking'];
    
    // 2. Chuyển đổi dữ liệu từng hàng
    const rows = allBookings.map(b => {
      return [
        b.id || (b as any)._id,
        formatDate(getBookingDate(b)),
        b.time,
        b.customerName,
        getPhone(b),
        getService(b),
        getEmployeeName(b),
        b.status === 'confirmed' ? 'Đã xác nhận' : b.status === 'completed' ? 'Hoàn thành' : b.status === 'cancelled' ? 'Đã hủy' : 'Chờ xác nhận',
        b.source === 'online' ? 'Trực tuyến' : 'Tại quầy'
      ].map(field => `"${String(field || '').replace(/"/g, '""')}"`) // Wrap in quotes to handle commas safely within fields
       .join(',');
    });

    // 3. Thêm UTF-8 BOM (\uFEFF) để Excel hiển thị Tiếng Việt đúng cách
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const exportFileDefaultName = `LichHen_${new Date().toISOString().split('T')[0]}.csv`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', encodedUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    document.body.appendChild(linkElement); // Required for Firefox
    linkElement.click();
    document.body.removeChild(linkElement);
  };

  // Reset filters - GIỮ NGUYÊN
  const resetFilters = () => {
    setFilters({
      timeRange: 'today',
      source: 'all',
      status: 'all',
      search: ''
    });
  };

  // Thêm nhanh booking (từ Booking List) - GIỮ NGUYÊN
  const quickAddBooking = () => {
    navigate('/dashboard');
    // Có thể truyền tham số để mở modal booking ở Dashboard
  };

  // Nút làm mới dữ liệu
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const [bookingsResponse, staffsResponse, servicesResponse] = await Promise.all([
        apiService.getBookings(),
        apiService.getEmployees(),
        apiService.getServices()
      ]);

      // apiService already returns parsed JSON
      const bookings = bookingsResponse.data || bookingsResponse || [];
      const staffsList = staffsResponse.data || staffsResponse || [];
      const servicesList = servicesResponse.data || servicesResponse || [];

      setAllBookings(bookings);
      setStaffs(staffsList);
      setServices(servicesList);
      calculateStats(bookings);
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert(language === 'vi'
        ? '⚠️ Không thể làm mới dữ liệu từ server'
        : '⚠️ Cannot refresh data from server'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="booking-list-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '500px' }}>
        <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', color: '#667eea', fontWeight: '500' }}>{t('schedule.loading') || 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div className="booking-list-container">
      {/* Header */}
      <div className="booking-header">
        <div className="header-left">
          {/* Calendar+Clock hero icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              flexShrink: 0,
              filter: 'drop-shadow(0 4px 12px rgba(77,166,245,0.25))'
            }}>
              <CalendarClockLargeIcon size={72} />
            </div>
            <div>
              <h1 style={{ margin: 0 }}>{t('bookings.title')}</h1>
              <p className="page-subtitle" style={{ margin: '4px 0 0' }}>
                {t('bookings.filter.all')}: {stats.total} • Online: {stats.online} • Manual: {stats.manual}
              </p>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="action-btn secondary" onClick={() => navigate('/dashboard')}>
            ← {t('sidebar.dashboard')}
          </button>
          <button className="action-btn primary" onClick={() => setShowAddServiceForm(!showAddServiceForm)}>
            {showAddServiceForm ? `✖ ${t('bookings.serviceForm.closeBtn')}` : `+ ${t('bookings.serviceForm.addBtn')}`}
          </button>
          <button className="action-btn quick-add" onClick={quickAddBooking}>
            + {t('bookings.create')}
          </button>
          <button className="action-btn export" onClick={exportBookings}>
            📥 Export
          </button>
          <button className="action-btn refresh" onClick={handleRefresh} title="Refresh">
            🔄
          </button>
        </div>
      </div>

      {/* Form thêm dịch vụ - GIỮ NGUYÊN */}
      {showAddServiceForm && (
        <div className="add-service-form">
          <div className="form-header">
            <h3>➕ {t('bookings.headers.addService')}</h3>
            <p>{t('bookings.serviceForm.description')}</p>
          </div>
          <div className="form-body">
            <div className="form-group">
              <label>{t('bookings.serviceForm.name')} *</label>
              <input
                type="text"
                placeholder={t('services.messages.enterName')}
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                list="category-suggestions-add"
                placeholder="e.g. Manicure, Pedicure..."
                value={newServiceCategory}
                onChange={(e) => setNewServiceCategory(e.target.value)}
                className="form-input"
              />
              <datalist id="category-suggestions-add">
                {Array.from(new Set(services.map(s => (s as any).category || 'Other'))).map(cat => (
                  <option key={cat} value={cat} />
                ))}
                <option value="Manicure" />
                <option value="Pedicure" />
                <option value="Nail Art" />
                <option value="Acrylic" />
                <option value="Gel" />
                <option value="Waxing" />
                <option value="Other" />
              </datalist>
            </div>
            <div className="form-group">
              <label>{t('bookings.serviceForm.price')} *</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{
                  position: 'absolute', left: '12px', color: '#2ecc71',
                  fontWeight: 'bold', fontSize: '1rem', pointerEvents: 'none'
                }}>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={newServicePrice || ''}
                  onChange={(e) => setNewServicePrice(e.target.value === '' ? 0 : Number(e.target.value))}
                  className="form-input"
                  style={{ paddingLeft: '28px' }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('bookings.serviceForm.color')}</label>
              <div className="color-picker">
                {serviceColors.map((color) => (
                  <button
                    key={color}
                    className={`color-option ${newServiceColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewServiceColor(color)}
                    title={color}
                  />
                ))}
              </div>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
              <input
                type="checkbox"
                id="bestSellerAdd"
                checked={newServiceIsBestSeller}
                onChange={(e) => setNewServiceIsBestSeller(e.target.checked)}
                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
              />
              <label htmlFor="bestSellerAdd" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                👑 Best Seller (Dịch vụ bán chạy)
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button className="form-btn cancel" onClick={() => setShowAddServiceForm(false)}>
              {t('dashboard.modals.buttons.cancel')}
            </button>
            <button className="form-btn submit" onClick={handleAddService}>
              {t('bookings.serviceForm.addBtn')}
            </button>
          </div>
        </div>
      )}

      {/* Form chỉnh sửa dịch vụ */}
      {editingService && (
        <div className="add-service-form editing-mode">
          <div className="form-header">
            <h3>✏️ {t('bookings.actions.edit')}</h3>
            <p>{t('bookings.serviceForm.description')}</p>
          </div>
          <div className="form-body">
            <div className="form-group">
              <label>{t('bookings.serviceForm.name')} *</label>
              <input
                type="text"
                value={editingService.name}
                onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                list="category-suggestions-edit"
                placeholder="e.g. Manicure, Pedicure..."
                value={editingService.category || ''}
                onChange={(e) => setEditingService({ ...editingService, category: e.target.value })}
                className="form-input"
              />
              <datalist id="category-suggestions-edit">
                {Array.from(new Set(services.map(s => (s as any).category || 'Other'))).map(cat => (
                  <option key={cat} value={cat} />
                ))}
                <option value="Manicure" />
                <option value="Pedicure" />
                <option value="Nail Art" />
                <option value="Acrylic" />
                <option value="Gel" />
                <option value="Waxing" />
                <option value="Other" />
              </datalist>
            </div>
            <div className="form-group">
              <label>{t('bookings.serviceForm.price')} *</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{
                  position: 'absolute', left: '12px', color: '#2ecc71',
                  fontWeight: 'bold', fontSize: '1rem', pointerEvents: 'none'
                }}>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={editingService.price || ''}
                  onChange={(e) => setEditingService({ ...editingService, price: e.target.value === '' ? 0 : Number(e.target.value) })}
                  className="form-input"
                  style={{ paddingLeft: '28px' }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('bookings.serviceForm.duration')} ({language === 'vi' ? 'phút' : 'min'})</label>
              <input
                type="number"
                min="5"
                step="5"
                placeholder="30"
                value={(editingService as any).duration || ''}
                onChange={(e) => setEditingService({ ...editingService, duration: e.target.value === '' ? 30 : Number(e.target.value) } as any)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>{t('bookings.serviceForm.color')}</label>
              <div className="color-picker">
                {serviceColors.map((color) => {
                  const isSelected = editingService.color === color;
                  return (
                    <button
                      key={color}
                      className={`color-option ${isSelected ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setEditingService({ ...editingService, color })}
                      title={color}
                    />
                  );
                })}
              </div>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
              <input
                type="checkbox"
                id="bestSellerEdit"
                checked={(editingService as any).isBestSeller || false}
                onChange={(e) => setEditingService({ ...editingService, isBestSeller: e.target.checked } as any)}
                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
              />
              <label htmlFor="bestSellerEdit" style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                👑 Best Seller (Dịch vụ bán chạy)
              </label>
            </div>
          </div>
          <div className="form-actions">
            <button className="form-btn cancel" onClick={() => setEditingService(null)}>
              {t('dashboard.modals.buttons.cancel')}
            </button>
            <button className="form-btn submit" onClick={handleUpdateService}>
              {t('profile.buttons.save')}
            </button>
          </div>
        </div>
      )}

      {/* Danh sách dịch vụ hiện có - GIỮ NGUYÊN */}
      {services.length > 0 && (() => {
          // Group services by category for display
          const grouped = services.reduce((acc, service) => {
            const cat = (service as any).category || 'Other';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(service);
            return acc;
          }, {} as Record<string, Service[]>);

          // Add empty groups so they are displayed
          emptyGroups.forEach(groupName => {
            if (!grouped[groupName]) {
              grouped[groupName] = [];
            }
          });

          // Sort: categories with best sellers first
          const sortedEntries = Object.entries(grouped).sort(([catA, a], [catB, b]) => {
            const aHas = a.some(s => (s as any).isBestSeller);
            const bHas = b.some(s => (s as any).isBestSeller);
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            return catA.localeCompare(catB);
          });

          return (
            <div className="services-section">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h3>🎨 {t('bookings.headers.serviceList')}</h3>
                  <span className="service-count">{services.length} {t('sidebar.services').toLowerCase()}</span>
                </div>
                <button
                  className="action-btn quick-add"
                  onClick={handleCreateNewGroup}
                  style={{
                    fontSize: '0.85rem',
                    padding: '6px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                    cursor: 'pointer'
                  }}
                >
                  ➕ {language === 'vi' ? 'Thêm nhóm mới' : 'Add New Group'}
                </button>
              </div>
              {sortedEntries.map(([category, catServices]) => {
                const hasBestSeller = catServices.some(s => (s as any).isBestSeller);
                return (
                  <div key={category} style={{ marginBottom: '20px', position: 'relative' }}>
                    <div style={{
                      padding: '8px 14px',
                      background: hasBestSeller
                        ? 'linear-gradient(135deg, #fffbeb, #fef3c7)'
                        : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                      borderRadius: '8px',
                      marginBottom: '10px',
                      borderLeft: `4px solid ${hasBestSeller ? '#f59e0b' : '#667eea'}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {hasBestSeller && <span>🔥</span>}
                      <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>{category}</span>
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>({catServices.length})</span>
                      
                      {/* Plus button to add/move service to this group */}
                      <button
                        onClick={() => setActiveGroupAction(activeGroupAction === category ? null : category)}
                        style={{
                          background: activeGroupAction === category ? '#ef4444' : '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: activeGroupAction === category ? '0.75rem' : '1.1rem',
                          fontWeight: 'bold',
                          transition: 'all 0.2s',
                          marginLeft: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        title={language === 'vi' ? 'Gộp hoặc thêm dịch vụ vào nhóm' : 'Group or add services to this group'}
                      >
                        {activeGroupAction === category ? '✕' : '+'}
                      </button>

                      {/* Delete group button (red X) - do not allow deleting default 'Other' group */}
                      {category !== 'Other' && (
                        <button
                          onClick={() => handleDeleteGroup(category)}
                          style={{
                            background: '#ffe4e6',
                            color: '#e11d48',
                            border: '1px solid #fda4af',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            transition: 'all 0.2s',
                            marginLeft: '4px',
                            boxShadow: '0 2px 4px rgba(225, 29, 72, 0.1)'
                          }}
                          title={language === 'vi' ? 'Xóa nhóm này' : 'Delete this group'}
                        >
                          ✕
                        </button>
                      )}

                      {hasBestSeller && (
                        <span style={{
                          marginLeft: 'auto',
                          padding: '2px 8px',
                          background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                          color: 'white',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          borderRadius: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Popular</span>
                      )}
                    </div>

                    {/* Active Group Action Dialog / Dropdown */}
                    {activeGroupAction === category && (
                      <div style={{
                        position: 'absolute',
                        top: '40px',
                        left: '14px',
                        zIndex: 100,
                        padding: '12px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '280px'
                      }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                          {language === 'vi' ? `Nhóm: ${category}` : `Group: ${category}`}
                        </div>
                        <button
                          onClick={() => {
                            handleAddServiceToCategory(category);
                            setActiveGroupAction(null);
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)'
                          }}
                        >
                          ➕ {language === 'vi' ? 'Tạo dịch vụ mới trong nhóm này' : 'Create new service in this group'}
                        </button>
                        
                        {services.filter(s => ((s as any).category || 'Other') !== category).length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid #f1f5f9', paddingTop: '10px' }}>
                            <label style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                              📥 {language === 'vi' ? 'Gộp dịch vụ sẵn có vào đây:' : 'Merge existing service here:'}
                            </label>
                            <select
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val) {
                                  handleMoveServiceToCategory(val, category);
                                  setActiveGroupAction(null);
                                }
                              }}
                              defaultValue=""
                              style={{
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.8rem',
                                width: '100%',
                                background: '#f8fafc',
                                color: '#334155'
                              }}
                            >
                              <option value="">-- {language === 'vi' ? 'Chọn dịch vụ để di chuyển' : 'Select service to move'} --</option>
                              {services
                                .filter(s => ((s as any).category || 'Other') !== category)
                                .map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.name} ({s.category || 'Other'})
                                  </option>
                                ))
                              }
                            </select>
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                            {language === 'vi' ? 'Không còn dịch vụ nào ở nhóm khác' : 'No services in other groups'}
                          </div>
                        )}
                      </div>
                    )}

                    {catServices.length === 0 ? (
                      <div style={{
                        padding: '20px',
                        background: '#f8fafc',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '8px',
                        textAlign: 'center',
                        color: '#64748b',
                        fontSize: '0.85rem',
                        marginBottom: '10px'
                      }}>
                        {language === 'vi' 
                          ? 'Nhóm chưa có dịch vụ nào. Hãy click nút "+" ở trên để thêm hoặc gộp dịch vụ!'
                          : 'This group has no services yet. Click the "+" button above to add or merge services!'}
                      </div>
                    ) : (
                      <div className="services-grid">
                        {catServices.map(service => (
                        <div key={service.id} className="service-card">
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%' }}>
                            <div className="service-color" style={{ backgroundColor: service.color, marginTop: '2px' }}></div>
                            <div className="service-info">
                              <h4>
                                {service.name}
                                {(service as any).isBestSeller && (
                                  <span style={{
                                    marginLeft: '6px',
                                    padding: '2px 6px',
                                    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    borderRadius: '10px',
                                    verticalAlign: 'middle'
                                  }}>⭐ Best Seller</span>
                                )}
                              </h4>
                              <div className="service-meta" style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', marginTop: '4px', flexWrap: 'wrap' }}>
                                <span className="price-tag" style={{ color: '#2ecc71', fontWeight: 'bold' }}>${service.price}</span>
                                {(service as any).duration && (
                                  <span style={{ color: '#64748b' }}>⏱ {(service as any).duration}{language === 'vi' ? 'phút' : 'min'}</span>
                                )}
                                <span className="color-code" style={{ opacity: 0.6 }}>{service.color}</span>
                              </div>
                            </div>
                          </div>
                          <div className="service-card-actions" style={{ display: 'flex', gap: '8px', zIndex: 10, justifyContent: 'flex-end', marginTop: '4px', borderTop: '1px solid #e9ecef', paddingTop: '10px', width: '100%' }}>
                            <button
                              className="service-edit-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingService(service);
                              }}
                              title="Sửa dịch vụ"
                              style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem', padding: '6px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                            >
                              ✏️ {language === 'vi' ? 'Sửa' : 'Edit'}
                            </button>
                            <button
                              className="service-delete-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteService(service.id);
                              }}
                              title="Xóa dịch vụ"
                              style={{ background: '#fff0f0', border: '1px solid #ffc9c9', borderRadius: '4px', cursor: 'pointer', fontSize: '1.1rem', color: '#ff4757', padding: '6px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                            >
                              × {language === 'vi' ? 'Xóa' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()
      }

      {/* Thống kê nhanh - GIỮ NGUYÊN */}
      <div className="quick-stats">
        <div className="stat-item">
          <span className="stat-label">{t('bookings.stats.today')}</span>
          <span className="stat-value">{stats.today}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('bookings.stats.week')}</span>
          <span className="stat-value">{stats.week}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('bookings.stats.month')}</span>
          <span className="stat-value">{stats.month}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('bookings.stats.total')}</span>
          <span className="stat-value">{stats.total}</span>
        </div>
      </div>

      {/* Stats Grid - GIỮ NGUYÊN */}
      <div className="stats-grid">
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>{t('bookings.stats.pending')}</h3>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="stat-card confirmed">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{t('bookings.stats.confirmed')}</h3>
            <p className="stat-value">{stats.confirmed}</p>
          </div>
        </div>
        <div className="stat-card completed">
          <div className="stat-icon">🎉</div>
          <div className="stat-content">
            <h3>{t('bookings.stats.completed')}</h3>
            <p className="stat-value">{stats.completed}</p>
          </div>
        </div>
        <div className="stat-card cancelled">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <h3>{t('bookings.stats.cancelled')}</h3>
            <p className="stat-value">{stats.cancelled}</p>
          </div>
        </div>
      </div>

      {/* Filters - GIỮ NGUYÊN */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('bookings.filters.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label>{t('bookings.filters.time')}</label>
            <select
              className="filter-select"
              value={filters.timeRange}
              onChange={(e) => setFilters({ ...filters, timeRange: e.target.value })}
            >
              <option value="today">{t('bookings.filters.options.today')}</option>
              <option value="week">{t('bookings.filters.options.week')}</option>
              <option value="month">{t('bookings.filters.options.month')}</option>
              <option value="all">{t('bookings.filters.options.all')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('bookings.filters.source')}</label>
            <select
              className="filter-select"
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            >
              <option value="all">{t('bookings.filters.options.allSources')}</option>
              <option value="online">{t('bookings.filters.options.online')}</option>
              <option value="manual">{t('bookings.filters.options.manual')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('bookings.filters.status')}</label>
            <select
              className="filter-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="all">{t('bookings.filters.options.allStatuses')}</option>
              <option value="pending">{t('bookings.filter.pending')}</option>
              <option value="confirmed">{t('bookings.filter.confirmed')}</option>
              <option value="completed">{t('bookings.filter.completed')}</option>
              <option value="cancelled">{t('bookings.filter.cancelled')}</option>
            </select>
          </div>

          <button className="reset-btn" onClick={resetFilters}>
            🔄 {t('bookings.filters.reset')}
          </button>
        </div>
      </div>

      {/* Danh sách booking - GIỮ NGUYÊN */}
      <div className="bookings-section">
        <div className="section-header">
          <h2>
            {displayBookings.length === allBookings.length
              ? t('bookings.headers.allBookings')
              : `${t('bookings.headers.filteredResults')} (${displayBookings.length}/${allBookings.length})`
            }
          </h2>
          <div className="section-actions">
            <span className="results-count">{displayBookings.length} booking</span>
          </div>
        </div>

        {displayBookings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>{t('bookings.empty')}</h3>
            <p className="empty-hint">{t('bookings.hints.emptyState')}</p>
            <button className="empty-action-btn" onClick={() => navigate('/dashboard')}>
              📊 {t('bookings.hints.goToDashboard')}
            </button>
          </div>
        ) : (
          <div className="bookings-table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('bookings.table.customer')}</th>
                  <th>{t('employees.modals.labels.phone')}</th>
                  <th>{t('bookings.table.service')}</th>
                  <th>{t('bookings.table.staff')}</th>
                  <th>{t('bookings.table.date')}</th>
                  <th>{t('bookings.filters.source')}</th>
                  <th>{t('bookings.table.status')}</th>
                  <th>{t('bookings.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {displayBookings.map(booking => (
                  <tr key={booking.id} className={`booking-row status-${booking.status}`}>
                    <td className="booking-id">#{booking.id.toString().slice(-6)}</td>
                    <td className="customer-info">
                      <div className="customer-name" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {booking.customerName}
                        {booking.source === 'online' && <span className="online-indicator" style={{ fontSize: '0.8rem' }}>Online</span>}
                        {booking.noteImages && booking.noteImages.length > 0 && (
                          <span
                            title={`${booking.noteImages.length} photo(s) attached — click to view`}
                            onClick={() => setViewDetail(booking)}
                            style={{ fontSize: '0.75rem', background: '#ede9fe', color: '#7c3aed', padding: '1px 7px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
                          >
                            📷 {booking.noteImages.length}
                          </span>
                        )}
                      </div>
                      {getNotes(booking) && (
                        <div className="customer-notes">{getNotes(booking)}</div>
                      )}
                    </td>
                    <td className="customer-phone">
                      {getPhone(booking) || '--'}
                    </td>
                    <td>
                      <div className="service-badges-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {booking.services && booking.services.length > 0 ? (
                          booking.services.map((svc, idx) => (
                            <span key={idx} className="service-badge">{svc}</span>
                          ))
                        ) : (
                          <span className="service-badge">{getService(booking)}</span>
                        )}
                      </div>
                    </td>
                    {/* Nhan vien */}
                    <td>
                      <div className="staff-info">
                        {getEmployeeName(booking) || (
                          <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{t('bookings.unassigned')}</span>
                        )}
                      </div>
                    </td>
                    {/* Ngay gio */}
                    <td>
                      <div className="booking-datetime">
                        <div className="booking-date">{formatDate(getBookingDate(booking))}</div>
                        <div className="booking-time">{booking.time}</div>
                        {booking.createdAt && (
                          <div className="created-time">
                            {t('bookings.table.date').split('&')[0].trim()}: {new Date(booking.createdAt).toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US')}
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Nguon */}
                    <td>
                      <span className={`source-badge ${booking.source}`}>
                        {booking.source === 'online' ? 'Online' : t('bookings.filters.options.manual').split(' ')[0]}
                      </span>
                    </td>
                    {/* Status dropdown */}
                    <td>
                      <div className="status-control">
                        <select
                          className={`status-select status-${booking.status}`}
                          value={booking.status}
                          disabled={!canEdit}
                          onChange={(e) => canEdit && handleStatusChange(
                            ((booking.id || (booking as any)._id) as any).toString(),
                            e.target.value,
                            booking
                          )}
                          style={!canEdit ? { opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                        >
                          <option value="pending">{t('bookings.filter.pending')}</option>
                          <option value="confirmed">{t('bookings.filter.confirmed')}</option>
                          <option value="completed">{t('bookings.filter.completed')}</option>
                          <option value="cancelled">{t('bookings.filter.cancelled')}</option>
                        </select>
                      </div>
                    </td>
                    {/* Actions */}
                    <td>
                      <div className="action-buttons">
                        {/* Xem chi tiet */}
                        <button
                          className="action-btn view"
                          onClick={() => setViewDetail(booking)}
                          title={t('bookings.actions.viewDetails')}
                        >
                          i
                        </button>
                        {/* Phan cong — chi hien voi owner/manager/receptionist/admin */}
                        {canEdit && (
                          <button
                            className="action-btn edit"
                            style={{ background: '#667eea', color: '#fff', borderRadius: '6px', border: 'none', padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem' }}
                            onClick={() => {
                              setAssignBooking(booking);
                              setAssignEmpId((booking.employeeId || '').toString());
                            }}
                            title={t('bookings.actions.edit')}
                          >
                            u
                          </button>
                        )}
                        {/* Xoa — chi hien voi owner/manager/receptionist/admin */}
                        {canEdit && (
                          <button
                            className="action-btn delete"
                            onClick={() => deleteBooking((booking.id || (booking as any)._id) as any)}
                            title={t('bookings.actions.delete')}
                          >
                            x
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tom tat thong ke */}
      <div className="summary-section">
        <h3>📊 {t('bookings.summary.title')}</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">{t('bookings.summary.onlineBookings')}</span>
            <span className="summary-value">{stats.online}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">{t('bookings.summary.manualBookings')}</span>
            <span className="summary-value">{stats.manual}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">{t('bookings.summary.completionRate')}</span>
            <span className="summary-value">
              {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">{t('bookings.summary.cancelRate')}</span>
            <span className="summary-value">
              {stats.total > 0 ? Math.round((stats.cancelled / stats.total) * 100) : 0}%
            </span>
          </div>
        </div>
      </div>

      {/* ===== BOOKING DETAIL MODAL ===== */}
      {viewDetail && (
        <div className="modal-overlay" onClick={() => setViewDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', maxWidth: '520px', width: '95%', maxHeight: '92vh', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1.1rem' }}>{t('bookings.actions.viewDetails')}</h3>
                {viewDetail.source === 'online' && (
                  <span style={{ fontSize: '0.72rem', background: 'rgba(255,255,255,0.25)', color: '#fff', padding: '2px 8px', borderRadius: '10px', marginTop: '4px', display: 'inline-block' }}>
                    Online
                  </span>
                )}
              </div>
              <button onClick={() => setViewDetail(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>x</button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>{t('bookings.table.customer')}</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{viewDetail.customerName}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>{t('employees.modals.labels.phone')}</div>
                  <div style={{ fontWeight: 600 }}>{getPhone(viewDetail) || '--'}</div>
                </div>
                {viewDetail.customerEmail && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>Email</div>
                    <div>{viewDetail.customerEmail}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>{t('bookings.table.service')}</div>
                  <div style={{ fontWeight: 600 }}>{getService(viewDetail)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.modals.labels.people')}</div>
                  <div>{viewDetail.numberOfPeople || 1}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.modals.labels.date')}</div>
                  <div style={{ fontWeight: 600 }}>{getBookingDate(viewDetail)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.modals.labels.time')}</div>
                  <div style={{ fontWeight: 600 }}>{viewDetail.time}</div>
                </div>
                {getNotes(viewDetail) && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '4px' }}>{t('dashboard.modals.labels.notes')}</div>
                    <div style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: '8px', fontStyle: 'italic', borderLeft: '3px solid #667eea', fontSize: '0.9rem' }}>
                      {getNotes(viewDetail)}
                    </div>
                  </div>
                )}
                {/* Customer photos */}
                {viewDetail.noteImages && viewDetail.noteImages.length > 0 && (
                  <div style={{ gridColumn: '1/-1', background: '#f8fafc', borderRadius: '10px', padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#667eea', fontWeight: 700 }}>📷 {language === 'vi' ? 'Ảnh từ khách hàng' : 'Customer Photos'} ({viewDetail.noteImages.length})</div>
                      <div style={{ fontSize: '0.7rem', color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>⏳ {language === 'vi' ? 'Xóa sau 7 ngày' : 'Auto-deleted in 7 days'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {viewDetail.noteImages.map((url: string, idx: number) => (
                        <div key={idx} style={{ textAlign: 'center' }}>
                          <img
                            src={url}
                            alt={`Customer photo ${idx + 1}`}
                            onClick={() => setPreviewImg(url)}
                            style={{
                              width: 90, height: 90, objectFit: 'cover', borderRadius: '8px',
                              cursor: 'pointer', border: '2px solid #e2e8f0', display: 'block',
                              transition: 'transform 0.2s, border-color 0.2s'
                            }}
                            onMouseEnter={e => { (e.target as HTMLImageElement).style.transform = 'scale(1.05)'; (e.target as HTMLImageElement).style.borderColor = '#667eea'; }}
                            onMouseLeave={e => { (e.target as HTMLImageElement).style.transform = 'scale(1)'; (e.target as HTMLImageElement).style.borderColor = '#e2e8f0'; }}
                          />
                          <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '3px' }}>{idx + 1}/{viewDetail.noteImages!.length}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Status */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>{t('bookings.table.status')}</div>
                <select
                  value={viewDetail.status}
                  onChange={e => updateBookingStatus(((viewDetail.id || (viewDetail as any)._id) as any).toString(), e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '8px', border: '2px solid #e2e8f0',
                    fontSize: '0.95rem', cursor: 'pointer', fontWeight: 700,
                    background: viewDetail.status === 'pending' ? '#fff3cd' : viewDetail.status === 'confirmed' ? '#d1fae5' : viewDetail.status === 'completed' ? '#dbeafe' : '#fee2e2'
                  }}
                >
                  <option value="pending">{t('bookings.filter.pending')}</option>
                  <option value="confirmed">{t('bookings.filter.confirmed')}</option>
                  <option value="completed">{t('bookings.filter.completed')}</option>
                  <option value="cancelled">{t('bookings.filter.cancelled')}</option>
                </select>
              </div>
              {/* Nhan vien */}
              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                {t('bookings.table.staff')}: <strong>{getEmployeeName(viewDetail) || t('bookings.unassigned')}</strong>
              </div>
            </div>
            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewDetail(null)} style={{ padding: '8px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }}>
                {t('dashboard.modals.buttons.cancel')}
              </button>
              <button
                onClick={() => { setAssignBooking(viewDetail); setAssignEmpId((viewDetail.employeeId || '').toString()); setViewDetail(null); }}
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                {t('bookings.table.staff')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ASSIGN EMPLOYEE MODAL ===== */}
      {assignBooking && (
        <div className="modal-overlay" onClick={() => setAssignBooking(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', maxWidth: '420px', width: '95%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>{t('bookings.table.staff')}</h3>
              <button onClick={() => setAssignBooking(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}>x</button>
            </div>
            <div style={{ padding: '24px' }}>
              {/* Booking summary */}
              <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', borderLeft: '4px solid #667eea' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>{assignBooking.customerName}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{getService(assignBooking)} — {getBookingDate(assignBooking)} {assignBooking.time}</div>
                <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: '2px' }}>{getPhone(assignBooking)}</div>
                {getNotes(assignBooking) && (
                  <div style={{ marginTop: '8px', fontSize: '0.82rem', fontStyle: 'italic', color: '#475569', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                    📝 {getNotes(assignBooking)}
                  </div>
                )}
              </div>
              {/* Customer photos in assign modal */}
              {(assignBooking as any).noteImages?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600, marginBottom: '8px' }}>
                    📷 {language === 'vi' ? 'Ảnh khách gửi' : 'Customer Photos'} ({(assignBooking as any).noteImages.length})
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(assignBooking as any).noteImages.map((url: string, idx: number) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img
                          src={url}
                          alt={`Photo ${idx + 1}`}
                          onClick={() => setPreviewImg(url)}
                          style={{
                            width: 90, height: 90, objectFit: 'cover', borderRadius: '10px',
                            cursor: 'pointer', border: '2px solid #e2e8f0', display: 'block',
                            transition: 'transform 0.15s, border-color 0.15s'
                          }}
                          onMouseEnter={e => { (e.target as HTMLImageElement).style.transform = 'scale(1.06)'; (e.target as HTMLImageElement).style.borderColor = '#667eea'; }}
                          onMouseLeave={e => { (e.target as HTMLImageElement).style.transform = 'scale(1)'; (e.target as HTMLImageElement).style.borderColor = '#e2e8f0'; }}
                        />
                        <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.45)', borderRadius: '4px', padding: '1px 5px', fontSize: '0.65rem', color: '#fff' }}>
                          🔍
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Select nhan vien */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>{t('bookings.table.staff')}</label>
                <select
                  value={assignEmpId}
                  onChange={e => setAssignEmpId(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '0.95rem', background: '#f8fafc', cursor: 'pointer' }}
                >
                  <option value="">-- {t('bookings.unassigned')} --</option>
                  {staffs.map(s => (
                    <option key={(s.id || (s as any)._id)?.toString()} value={(s.id || (s as any)._id)?.toString()}>
                      {s.name} {(s as any).position ? `(${(s as any).position})` : ''}
                    </option>
                  ))}
                </select>
                {assignEmpId && (
                  <div style={{ marginTop: '8px', fontSize: '0.82rem', color: '#667eea' }}>
                    {t('bookings.table.staff')}: <strong>{staffs.find(s => (s.id || (s as any)._id)?.toString() === assignEmpId)?.name}</strong>
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAssignBooking(null)} style={{ padding: '8px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }}>
                {t('dashboard.modals.buttons.cancel')}
              </button>
              <button
                onClick={handleAssignEmployee}
                disabled={assigning}
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: assigning ? 0.7 : 1 }}
              >
                {assigning ? t('profile.buttons.saving') : t('dashboard.modals.buttons.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === CHECKOUT MODAL: Ghi nhận doanh thu khi hoàn thành === */}
      {checkoutBooking && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '16px'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px',
            maxWidth: '480px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                  💳 {t('bookings.modals.checkout.title')}
                </h2>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8', marginTop: '4px' }}>
                  {t('bookings.modals.checkout.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setCheckoutBooking(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}
              >×</button>
            </div>
            {/* Booking summary */}
            <div style={{ padding: '24px' }}>
              <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px 16px', marginBottom: '24px', borderLeft: '4px solid #10b981' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>{checkoutBooking.customerName}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {getService(checkoutBooking)} — {getBookingDate(checkoutBooking)} {checkoutBooking.time}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: '2px' }}>{getPhone(checkoutBooking)}</div>
                {getEmployeeName(checkoutBooking) && (
                  <div style={{ fontSize: '0.82rem', color: '#667eea', marginTop: '4px' }}>
                    👤 {getEmployeeName(checkoutBooking)}
                  </div>
                )}
              </div>

              {/* Price inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem', color: '#374151' }}>
                    💵 {t('bookings.modals.checkout.servicePrice')} ($)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={checkoutPrice}
                    onChange={e => setCheckoutPrice(Number(e.target.value))}
                    style={{
                      width: '100%', padding: '10px 14px',
                      borderRadius: '10px', border: '2px solid #e2e8f0',
                      fontSize: '1.1rem', fontWeight: 700, color: '#1a202c',
                      boxSizing: 'border-box', outline: 'none'
                    }}
                    onFocus={e => e.target.style.border = '2px solid #667eea'}
                    onBlur={e => e.target.style.border = '2px solid #e2e8f0'}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem', color: '#374151' }}>
                    💝 {t('bookings.modals.checkout.tipAmount')} ($)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={checkoutTip}
                    onChange={e => setCheckoutTip(Number(e.target.value))}
                    style={{
                      width: '100%', padding: '10px 14px',
                      borderRadius: '10px', border: '2px solid #e2e8f0',
                      fontSize: '1.1rem', fontWeight: 700, color: '#1a202c',
                      boxSizing: 'border-box', outline: 'none'
                    }}
                    onFocus={e => e.target.style.border = '2px solid #10b981'}
                    onBlur={e => e.target.style.border = '2px solid #e2e8f0'}
                  />
                </div>
              </div>

              {/* Total */}
              <div style={{
                background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                borderRadius: '10px', padding: '14px 18px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span style={{ fontWeight: 600, color: '#065f46' }}>{t('bookings.modals.checkout.totalIncome')}:</span>
                <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#059669' }}>
                  ${(checkoutPrice + checkoutTip).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCheckoutBooking(null)}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }}
              >
                {t('dashboard.modals.buttons.cancel')}
              </button>
              <button
                onClick={handleCheckoutConfirm}
                disabled={checkoutLoading}
                style={{
                  padding: '10px 24px', borderRadius: '8px', border: 'none',
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem',
                  opacity: checkoutLoading ? 0.7 : 1
                }}
              >
                {checkoutLoading ? `⏳ ${t('profile.buttons.saving')}` : `✅ ${t('bookings.modals.checkout.confirmBtn')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== IMAGE LIGHTBOX ===== */}
      {previewImg && (
        <div
          onClick={() => setPreviewImg(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={() => setPreviewImg(null)}
            style={{
              position: 'absolute', top: 16, right: 20,
              background: 'rgba(255,255,255,0.15)', border: 'none',
              color: '#fff', fontSize: '1.8rem', cursor: 'pointer',
              borderRadius: '50%', width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1
            }}
          >×</button>
          <img
            src={previewImg}
            alt="Preview"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '95vw', maxHeight: '90vh',
              objectFit: 'contain', borderRadius: '12px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              cursor: 'default'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default BookingList;

